<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface Props {
  type?: ToastType
  message: string
  duration?: number
  dismissible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 5000,
  dismissible: true,
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const visible = ref(true)
let timer: ReturnType<typeof setTimeout> | null = null

const icons = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
}

const typeClasses = {
  success: 'border-l-success bg-success-light',
  error: 'border-l-danger bg-danger-light',
  warning: 'border-l-warning bg-warning-light',
  info: 'border-l-info bg-info-light',
}

const iconClasses = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
}

const close = () => {
  visible.value = false
  setTimeout(() => {
    emit('close')
  }, 300)
}

onMounted(() => {
  if (props.duration > 0) {
    timer = setTimeout(close, props.duration)
  }
})

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer)
  }
})
</script>

<template>
  <Transition name="toast">
    <div
      v-if="visible"
      role="alert"
      aria-live="polite"
      :class="[
        'flex items-start gap-3 p-4 glass rounded-xl shadow-glass-lg border-l-4 max-w-sm',
        typeClasses[type],
      ]"
    >
      <span :class="['text-lg font-bold', iconClasses[type]]">
        {{ icons[type] }}
      </span>
      
      <div class="flex-1 min-w-0">
        <p class="text-sm text-default">{{ message }}</p>
      </div>
      
      <button
        v-if="dismissible"
        type="button"
        class="transition-colors text-muted"
        @click="close"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
